import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchResumeData } from '../../../fetchProjects';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

/* ── Group flat list by organization (case-insensitive) ─────────────────── */
const groupByOrg = (list) => {
  const map = new Map();
  list.forEach((exp) => {
    const key = (exp.organization || '').trim().toLowerCase() || `__solo__${exp.id}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        org:      exp.organization || '',
        logo:     exp.badge?.startsWith('http') ? exp.badge : null,
        location: exp.location || '',
        roles:    [],
      });
    }
    map.get(key).roles.push(exp);
  });
  return Array.from(map.values());
};

/* ── Pill badges (date + label badges) ─────────────────────────────────── */
const Badges = ({ exp }) => {
  const rawSlug = exp.slug ? exp.slug.split('___')[0] : '';
  const labelBadges = rawSlug.trim().length > 0 && rawSlug.length < 120
    ? rawSlug.split(',').map(b => b.trim()).filter(Boolean)
    : [];

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {(exp.date_value || exp.duration) && (
        <span className="bg-white/5 border border-white/10 text-text-secondary text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap">
          {exp.date_value || exp.duration}
        </span>
      )}
      {labelBadges.map((b, i) => (
        <span key={i} className="bg-accent/10 border border-accent/20 text-accent-light text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
          {b}
        </span>
      ))}
    </div>
  );
};

/* ── Role detail body: description, achievements, tech, link ────────────── */
const RoleBody = ({ exp }) => (
  <div className="mt-2 space-y-3">
    {exp.description && (
      <p className="text-text-secondary text-sm leading-relaxed">{exp.description}</p>
    )}

    {exp.achievements?.length > 0 && (
      <ul className="space-y-1.5">
        {exp.achievements.map((ach, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-text-secondary">
            <div className="w-1.5 h-1.5 rounded-full bg-success-light flex-shrink-0 mt-[6px] shadow-[0_0_4px_rgba(34,197,94,0.4)]" />
            <span className="leading-snug">{ach}</span>
          </li>
        ))}
      </ul>
    )}

    {exp.technologies?.length > 0 && (
      <div className="flex flex-wrap gap-1.5">
        {exp.technologies.map((t, i) => (
          <span key={i} className="bg-white/5 border border-accent/15 text-accent-light/80 px-2.5 py-0.5 rounded-md text-xs font-medium">
            {t}
          </span>
        ))}
      </div>
    )}

    {exp.detailedContent && (
      <div>
        <Link
          to={`/resume/${exp.id}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:text-accent-light transition-smooth group/link"
          aria-label={`View full details for ${exp.title} at ${exp.organization}`}
        >
          View Details
          <Icon name="ArrowRight" size={12} className="group-hover/link:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    )}
  </div>
);

/* ── Unified Experience Card ─────────────────────────────────────────────
   Every entry — single role or multi-role — uses this SAME card shell.
   Structure:
     ┌───────────────────────────────────────────────────────┐
     │  [Logo]  Company Name  ·  Location                    │  ← always shown
     │  ────────────────────────────────────────────────     │  ← divider
     │  ●  Role Title                     Date  [Badge]      │  ← role(s)
     │     Description · Achievements · Tech tags            │
     └───────────────────────────────────────────────────────┘
*/
const ExperienceCard = ({ group }) => {
  const isGrouped = group.roles.length > 1;

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 hover:bg-white/[0.05] transition-all duration-300 group">

      {/* ── Company header ───────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-5 pt-5 pb-4">
        {/* Logo */}
        {group.logo ? (
          <div className="w-12 h-12 flex-shrink-0 bg-white rounded-xl flex items-center justify-center p-1.5 overflow-hidden shadow-sm border border-white/10">
            <Image src={group.logo} alt={group.org} className="w-full h-full object-contain" />
          </div>
        ) : (
          <div className="w-12 h-12 flex-shrink-0 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center">
            <Icon name="Building2" size={20} className="text-text-muted" />
          </div>
        )}

        {/* Org name + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading font-bold text-base text-text-primary leading-tight">
              {group.org}
            </h3>
            {isGrouped && (
              <span className="inline-flex items-center gap-1 bg-accent/10 border border-accent/20 text-accent-light text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                <Icon name="Layers" size={9} />
                {group.roles.length} roles
              </span>
            )}
          </div>
          {group.location && (
            <p className="text-text-muted text-xs mt-0.5 flex items-center gap-1">
              <Icon name="MapPin" size={10} />
              {group.location}
            </p>
          )}
        </div>
      </div>

      {/* ── Divider ──────────────────────────────────────────────────── */}
      <div className="h-px bg-white/[0.06] mx-5" />

      {/* ── Roles ────────────────────────────────────────────────────── */}
      <div className="px-5 pt-4 pb-5">
        {isGrouped ? (
          /* Multi-role: vertical timeline */
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-[5px] top-2.5 bottom-4 w-px bg-gradient-to-b from-accent/40 via-white/10 to-transparent" />

            <div className="space-y-5">
              {group.roles.map((exp, idx) => (
                <div key={exp.id} className="flex items-start gap-4">
                  {/* Timeline dot */}
                  <div className="flex-shrink-0 pt-0.5">
                    <div className={`w-2.5 h-2.5 rounded-full border-2 mt-1 ${
                      idx === 0
                        ? 'bg-accent border-accent shadow-[0_0_8px_rgba(14,165,233,0.6)]'
                        : 'bg-background border-white/25'
                    }`} />
                  </div>

                  {/* Role content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-semibold text-sm text-text-primary leading-snug">
                          {exp.title}
                        </h4>
                        {exp.location && exp.location !== group.location && (
                          <p className="text-text-muted text-xs mt-0.5 flex items-center gap-1">
                            <Icon name="MapPin" size={9} /> {exp.location}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <Badges exp={exp} />
                      </div>
                    </div>
                    <RoleBody exp={exp} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Single role — same structure, no dot/timeline */
          <div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-heading font-semibold text-base text-text-primary leading-snug">
                  {group.roles[0].title}
                </h4>
              </div>
              <div className="flex-shrink-0">
                <Badges exp={group.roles[0]} />
              </div>
            </div>
            <RoleBody exp={group.roles[0]} />
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Skeleton loader ─────────────────────────────────────────────────────── */
const Skeleton = () => (
  <div className="space-y-4 animate-pulse">
    {[1, 2, 3].map(i => (
      <div key={i} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-11 h-11 bg-white/8 rounded-xl flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-white/8 rounded w-1/2" />
            <div className="h-3 bg-white/5 rounded w-1/4" />
          </div>
        </div>
        <div className="h-px bg-white/5 mb-4" />
        <div className="space-y-2">
          <div className="h-4 bg-white/5 rounded w-3/4" />
          <div className="h-3 bg-white/5 rounded w-full" />
          <div className="h-3 bg-white/5 rounded w-5/6" />
        </div>
      </div>
    ))}
  </div>
);

/* ── Main component ──────────────────────────────────────────────────────── */
const WorkExperience = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showAll, setShowAll]         = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    fetchResumeData()
      .then(data => setExperiences((data || []).filter(i => i.type === 'experience')))
      .catch(err => console.error('Failed to fetch experiences:', err))
      .finally(() => setLoading(false));
  }, []);

  const groups        = groupByOrg(experiences);
  const SHOW_INITIAL  = 4;
  const visibleGroups = showAll ? groups : groups.slice(0, SHOW_INITIAL);
  const hiddenCount   = groups.length - SHOW_INITIAL;

  const handleToggle = () => {
    if (showAll && sectionRef.current) {
      window.scrollTo({
        top: sectionRef.current.getBoundingClientRect().top + window.scrollY - 110,
        behavior: 'smooth',
      });
    }
    setShowAll(p => !p);
  };

  return (
    <section
      ref={sectionRef}
      className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8 mb-8 relative overflow-hidden shadow-2xl"
    >
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-30 pointer-events-none" />

      {/* Section title */}
      <div className="flex items-center gap-3 mb-6 relative z-10">
        <div className="w-1 h-7 bg-accent rounded-full shadow-[0_0_8px_rgba(14,165,233,0.4)]" />
        <h2 className="font-heading font-bold text-2xl lg:text-3xl text-text-primary">
          Professional Experience
        </h2>
      </div>

      {/* Cards */}
      <div className="relative z-10">
        {loading ? (
          <Skeleton />
        ) : experiences.length === 0 ? (
          <p className="text-text-muted text-center py-12">No experience entries found.</p>
        ) : (
          <div className="space-y-4">
            {visibleGroups.map(group => (
              <ExperienceCard key={group.key} group={group} />
            ))}
          </div>
        )}

        {/* Show more / less */}
        {!loading && groups.length > SHOW_INITIAL && (
          <div className="mt-6 pt-5 border-t border-white/8 flex justify-center">
            <button
              onClick={handleToggle}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-accent/30 rounded-full px-7 py-2.5 text-accent hover:text-accent-light text-sm font-semibold transition-all duration-300 group"
              aria-label={showAll ? 'Show fewer experiences' : 'Show all experiences'}
            >
              <Icon
                name={showAll ? 'ChevronUp' : 'ChevronDown'}
                size={14}
                className="transition-transform duration-200 group-hover:translate-y-0.5"
              />
              {showAll
                ? 'Show Less'
                : `Show ${hiddenCount} More ${hiddenCount === 1 ? 'Company' : 'Companies'}`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default WorkExperience;
